"use client";
import React, { useEffect } from "react";
import CourseHeader from "@/components/CourseContent/SideRailHero/CourseHeader";
import InstructorList from "@/components/CourseContent/ContentRefresh/InstructorList";
import { ProcessedCourse, EnrichedProgram } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { Instructor } from "@/common/instructor/types";
import { ProductViewTracker } from "@/components/ProductViewTracker";
import { useCourseCheckoutUrl } from "@/hooks/useCourseCheckoutUrl";
import { useCompareCtaUrl } from "@/components/ProductCompare/useCompareCtaUrl";
import Curriculum from "@/components/CourseContent/Curriculum";
import LearnerTestimonials from "@/components/CourseContent/LearnerTestimonials";
import WhatYoullLearn from "@/components/CourseContent/ContentRefresh/WhatYoullLearn";
import CourseAbout from "@/components/CourseContent/ContentOverview";
import EnterpriseCTA from "@/components/CourseContent/ContentRefresh/EnterpriseCTA";
import CourseAwards from "@/components/CourseContent/templates/components/Awards";
import isCourseArchived from "@/lib/course/utils/isCourseArchived";
import MidPageCTA from "@/components/CourseContent/ContentRefresh/MidPageCTA";
import SocialShare from "@/components/SocialShare";
import FactoidBar from "@/components/CourseContent/ContentRefresh/FactoidBar";
import StickyCTA from "@/components/CourseContent/ContentRefresh/StickyCTA";
import FAQs from "@/components/CourseContent/ContentRefresh/FAQs";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useUserEnrollments from "@/components/CourseContent/CourseContext/hooks/useUserEnrollments";
import useUserEntitlements from "@/components/CourseContent/CourseContext/hooks/useUserEntitlements";
import isUserEnrolled from "@/lib/course/utils/isUserEnrolled";
import MoreAboutCourse from "@/components/CourseContent/ContentRefresh/MoreAboutCourse";
import ISO6391 from "iso-639-1";
import {
  fullWidthContainer,
  mainContent,
  mainContentSideRail,
  maxWidthContainer,
} from "@/components/CourseContent/styles/constants";
import styles from "@/components/CourseContent/styles/contentHero.module.css";
import useLeadWithPaid from "@/monarch/rules/leadWithPaid/useLeadWithPaid";
import WaysToTakeCourse from "@/components/CourseContent/ContentRefresh/WaysToTakeCourse";
import CertificateWaysToTakeCourse from "@/components/Certificate/CertificateWaysToTakeCourse";
import CourseEnrollButton from "../../ContentRefresh/CourseEnroll/CourseEnrollButtons";
import EnrollLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import AuditLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/AuditLink";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import CertificateEmphasisTabs from "@/components/Certificate/CertificateEmphasisTabs";
import CertificateEmphasis from "@/components/Certificate/CertificateEmpashis";
import { RedesignedTrackComparison } from "@/components/Certificate/constants/enums";
import OCPromoBanner from "@/components/CourseContent/ContentRefresh/OCPromoBanner";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import { useCourseDetailPricingData } from "@/lib/checkout-geo-pricing";
import OFACMessage from "@/components/CourseContent/ContentRefresh/OFACmessage";
import PartnerProgramRecommendations from "@/components/ParterProgramRecommendations";
import { getAiTranslationLanguageLabels } from "@/lib/course/utils/getAiLanguages";
import useCourseDetailsData from "@/components/CourseContent/CourseContext/hooks/useCourseDetailsData";
import RelatedProgramHeroSideRail from "@/components/CourseContent/templates/common/utils/renderRelatedProgramHeroSideRail";
import HeroSideRailContainer from "@/components/SideRail/HeroSideRail/HeroSideRailContainer";
import SideRailMobileContent from "@/components/SideRail/HeroSideRail/SideRailContent/MobileContent";
import useSideRailComponentList from "@/components/SideRail/HeroSideRail/useSideRailComponentList";
import { getPartnersDisplayNames } from "@/lib/partner/utils/getPartnerDisplayName";
import BenefitsModule from "@/components/BenefitsModule";
import PartnerInstructors from "@/components/PartnerInstructors";

import BundleSavingsBanner from "@/components/BundleSavingsBanner/BundleSavingsBanner";
import { getProgramSearchUrl } from "../common/utils/getProgramSearchUrl";
import CareerCards from "@/components/CareerCards";
import {
  CAREER_CARDS_VARIANTS,
  resolveCareerCardSkills,
  getIndustryKey,
} from "@/components/CareerCards/constants";
import { courseDetailPageEventConfig } from "../common/const/const";
import { useGetIsSideRailShown } from "@/hooks/useGetIsSideRailShown";
import SocialProof from "@/components/SocialProof";
import { headerStylesInDetailPages } from "@/components/SocialProof/const";
import ProductCompare from "@/components/ProductCompare";
import useCompareFallbackUuids from "@/components/ProductCompare/useCompareFallbackUuids";
import { CourseCurrencyBarSurface } from "@/components/CourseContent/templates/components/CourseCurrencySurfaces";
import { getMainContentSectionsOrder } from "../common/utils/getMainContentSectionsOrder";
import RecentlyViewedProductCardList from "@/components/RecentlyViewedProductCardList";
import useTranslations from "@/i18n/useTranslations";

type Props = {
  course: ProcessedCourse;
  program?: Program | null;
  enrichedPrograms?: EnrichedProgram[] | null;
  programPacingType?: string;
  programDuration?: string;
  hoursPerWeekFormatted?: string;
  paymentOption: "free" | "upgradable" | "paid" | null;
  showCourseModes: boolean;
  category?: string;
  courseSlug?: string;
  enrichedInstructors?: Instructor[];
};

export default function HeroSideRailCourseContent({
  course,
  enrichedPrograms,
  showCourseModes,
  paymentOption,
  category,
  courseSlug,
  enrichedInstructors,
}: Props) {
  const tCourse = useTranslations("Course");
  const tProgram = useTranslations("Program");
  //fetch enrollment data
  const { enrollments: userEnrollments } = useClientEnrollments();

  //fetch entitlement data
  const { entitlements: userEntitlements } = useClientEntitlements();

  const { setIsUserEnrolled, setUserEnrollments } = useUserEnrollments();
  const { setUserEntitled } = useUserEntitlements();

  const { setCourseDetailsData } = useCourseDetailsData();

  useEffect(() => {
    setCourseDetailsData({
      category: category,
      courseSlug: courseSlug,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, courseSlug]);

  useEffect(() => {
    if (userEntitlements && userEntitlements.length > 0) {
      const found =
        userEntitlements?.some(
          ({ courseUuid: userEntitledCourseUuid }) =>
            userEntitledCourseUuid === course.activeCourseRun?.uuid,
        ) || false;
      setUserEntitled(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEntitlements]);

  //set context enrollmment status
  useEffect(() => {
    if (userEnrollments && userEnrollments.length > 0) {
      setUserEnrollments(userEnrollments);
      const courseIds = course.activeCourseRuns?.map(({ key }) => key) || [];
      const isEnrolled = isUserEnrolled(userEnrollments, courseIds);
      setIsUserEnrolled(isEnrolled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEnrollments]);

  // Generate checkout URL with SKU and course run key
  const checkoutUrl = useCourseCheckoutUrl(course);
  const compareCtaUrl = useCompareCtaUrl(course);

  const { syllabusHtml, productFullDescription, video, productImageUrl } =
    course;
  const {
    weeksToComplete,
    minEffort,
    maxEffort,
    languageId,
    transcriptLanguages,
    seats,
  } = course.activeCourseRun || {};
  const courseHeaderVideo = {
    image: {
      src: productImageUrl,
    },
    src: video.src,
  };
  const titlePartner = getPartnersDisplayNames(course.partners);
  const courseTitle = `${titlePartner}: ${course.productName}`;
  const isArchived = course.activeCourseRun
    ? isCourseArchived(course.activeCourseRun)
    : true;
  const formattedLanguage = languageId
    ? ISO6391.getNativeName(languageId.slice(0, 2))
    : undefined;
  const formattedTranscripts =
    transcriptLanguages?.map((transcript) =>
      ISO6391.getNativeName(transcript.slice(0, 2)),
    ) || [];

  const formattedAiLanguages = course.activeCourseRun
    ? getAiTranslationLanguageLabels(course.activeCourseRun)
    : [];

  const leadWithPaidData = useLeadWithPaid(course);

  const pricingData = useCourseDetailPricingData({ course });
  const coursePrice =
    pricingData?.discountedPrice ?? pricingData?.originalPrice ?? null;

  const {
    headerBackground,
    sectionsOrder,
    isLoading,
    factoidsBar,
    hidePrice,
    pathInfo,
    emphasisModuleVersion,
    showRedesignedTrackComparison,
    information,
    stats,
    testimonial,
    tableDescription,
    tabsOrder,
    relatedPrograms,
    carouselData,
    carouselDataDesign3,
    promoBanner,
    stepComponent,
    showSideRail,
    sideRailHeaderComponents,
    sideRailBodyComponents,
    sideRailCollapsedHeaderComponents,
    sideRailCollapsedBodyComponents,
    sideRailMobileHeaderComponents,
    sideRailMobileBodyComponents,
    sideRailImage,
    sideRailBulletList,
    sideRailTitle,
    sideRailProductTitle,
    sideRailRating,
    sideRailSkillsList,
    sideRailVideoPlayer,
    sideRailImageTitle,
    sideRailStatisticComponent,
    relatedProgramsRedesign: {
      design2: expandableSection,
      designToDisplay,
      cardType,
    } = {},
    benefitsModule,
    testimonialsModule,
    bundleSavingsBanner,
    careerCards,
    productCompare,
    mainContent: mainContentFromTrafficConfig,
    straightToCheckout,
    currencySelector,
  } = useSingleCourseTrafficConfig({ course });

  const compareFallbackUuids = useCompareFallbackUuids({
    source: productCompare?.relatedProductsSource,
    relatedProductsFilters: productCompare?.relatedProductsFilters,
    enrichedPrograms,
    course,
  });

  const { isSideRailVisible } = useGetIsSideRailShown(showSideRail);

  const sideRailComponentList = useSideRailComponentList({
    productData: course,
    programIsEnrollable: false,
    courses: [],
    skus: [],
    pricingData,
    sideRailImage,
    sideRailBulletList,
    sideRailTitle,
    sideRailRating,
    sideRailSkillsList,
    sideRailVideoPlayer,
    sideRailImageTitle,
    sideRailStatisticComponent,
    straightToCheckout,
    currencySelector,
  });

  type HeaderBackground = "control" | "light" | "green";
  const BACKGROUND_CLASSES = {
    control: styles.contentHero,
    light: "bg-putty-200",
    green: "bg-secondary-500",
  };

  const getBackgroundClass = (bg?: HeaderBackground | undefined): string => {
    return bg
      ? (BACKGROUND_CLASSES[bg] ?? styles.contentHero)
      : styles.contentHero;
  };
  const breadcrumbVariant = headerBackground === "light" ? "dark" : "light";

  const componentList = [
    {
      key: "promoBanner",
      renderer: () => {
        return promoBanner && course.activeCourseRun?.isEnrollable ? (
          <OCPromoBanner
            course={course}
            courseRun={course.activeCourseRun}
            data={promoBanner}
            variant="slimWider"
            sideRail={!!showSideRail}
          />
        ) : null;
      },
    },
    {
      key: "whatYoullLearn",
      renderer: () => {
        return (
          <WhatYoullLearn
            key="whatYoullLearn"
            title={tCourse("whatYoullLearn")}
            parseContent={course.productOverview}
          />
        );
      },
    },
    {
      key: "courseAbout",
      renderer: () => {
        return (
          <CourseAbout
            key="courseAbout"
            courseData={course}
            fullDescription={productFullDescription}
            video={courseHeaderVideo}
            isVideoInSideRail={showSideRail && video.src !== ""}
          />
        );
      },
    },
    {
      key: "emphasisModuleVersion",
      renderer: () => {
        return showCourseModes &&
          !isArchived &&
          course?.activeCourseRun?.isEnrollable &&
          emphasisModuleVersion &&
          emphasisModuleVersion !== RedesignedTrackComparison.Hidden ? (
          <div className={maxWidthContainer}>
            {emphasisModuleVersion === RedesignedTrackComparison.Static ? (
              <CertificateEmphasis
                key="certificateEmphasis"
                course={course}
                courseRun={course?.activeCourseRun}
                information={information}
                stats={stats}
                testimonial={testimonial}
                hidePrice={hidePrice}
              />
            ) : (
              <CertificateEmphasisTabs
                key="certificateEmphasisTabs"
                information={information}
                stats={stats}
                testimonial={testimonial}
                pathInfoCoursePrice={coursePrice}
                tabOrder={tabsOrder}
                hidePrice={hidePrice}
                pathInfo={pathInfo}
                course={course}
                ctaElementRenderer={(tabId: string, ctaClassName: string) => {
                  return (
                    <>
                      {coursePrice ? (
                        <CourseEnrollButton
                          buttonClass={ctaClassName}
                          courseRun={course.activeCourseRun!}
                          course={course}
                          location={`cert-module-${tabId}`}
                          price={coursePrice}
                        />
                      ) : (
                        <EnrollLink
                          buttonClass={ctaClassName}
                          courseRun={course.activeCourseRun!}
                          course={course}
                          location={`cert-module-${tabId}`}
                        />
                      )}
                      <AuditLink
                        courseRun={course.activeCourseRun!}
                        course={course}
                        className={ctaClassName}
                        location={`cert-module-${tabId}`}
                      />
                    </>
                  );
                }}
              />
            )}
          </div>
        ) : null;
      },
    },
    {
      key: "careerCards",
      renderer: () => {
        const category = getIndustryKey({
          title: course.productName,
          subjectEn: course.subjectEn || "",
          subjectEs: course.subjectEs || "",
          overview: course.productOverview,
          subjects: course.subjects,
          skills: course.skills,
        });
        const careerCardsData =
          CAREER_CARDS_VARIANTS[category || "dataScience"];
        return (
          <CareerCards
            location={careerCards?.location || "detail-page"}
            isSideRail={careerCards?.isSideRail || true}
            industry={careerCards?.industry || category || "dataScience"}
            variant={careerCards?.variantKey || "default"}
            heading={careerCards?.heading || ""}
            title={careerCards?.title || careerCardsData.heading}
            stats={(careerCards?.stats || careerCardsData.cards).slice()}
            skills={resolveCareerCardSkills(
              careerCards?.skills,
              course.skills,
              category || "dataScience",
            ).slice()}
            hiddenCta={true}
          />
        );
      },
    },
    {
      key: "curriculum",
      renderer: () => {
        const hasPrerequisites = (course.prerequisitesHtml?.length ?? 0) > 0;
        if (!syllabusHtml && !hasPrerequisites) {
          return null;
        }
        return (
          <Curriculum
            key="curriculum"
            syllabus={syllabusHtml ?? ""}
            weeksToComplete={weeksToComplete}
            minEffort={minEffort}
            maxEffort={maxEffort}
            formattedLanguage={formattedLanguage}
            formattedTranscripts={formattedTranscripts}
            formattedAiLanguages={formattedAiLanguages}
            prerequisitesHtml={course.prerequisitesHtml}
          />
        );
      },
    },
    {
      key: "benefitsModule",
      renderer: () => {
        return benefitsModule ? (
          <BenefitsModule
            location="detail-page"
            schoolName={titlePartner}
            benefitsModule={benefitsModule}
          />
        ) : null;
      },
    },
    {
      key: "featuredInstructor",
      renderer: () => {
        return enrichedInstructors ? (
          <PartnerInstructors
            key="featuredInstructor"
            instructors={enrichedInstructors}
          />
        ) : null;
      },
    },
    {
      key: "courseAwards",
      renderer: () => {
        return course.customizations.catalogPage?.awards?.length ? (
          <CourseAwards
            key="courseAwards"
            awards={course.customizations.catalogPage.awards}
            isSpanish={false}
          />
        ) : null;
      },
    },
    {
      key: "midPageCta",
      renderer: () => {
        return (
          <MidPageCTA
            key="midPageCta"
            course={course}
            isArchived={isArchived}
          />
        );
      },
    },
    {
      key: "fullProgramCta",
      renderer: () =>
        designToDisplay && ["1", "2", "3"].includes(designToDisplay) ? (
          <div className={`${maxWidthContainer} w-full`}>
            <RelatedProgramHeroSideRail
              designTobeSelected={designToDisplay}
              enrichedPrograms={enrichedPrograms || undefined}
              course={course}
              carouselData={carouselData}
              carouselDataDesign3={carouselDataDesign3}
              cardType={cardType}
              expandableSection={expandableSection}
            />
          </div>
        ) : null,
    },
    {
      key: "learnerTestimonials",
      renderer: () => {
        return course.learnerTestimonials ? (
          <LearnerTestimonials
            key="learnerTestimonials"
            testimonials={course.learnerTestimonials}
          />
        ) : null;
      },
    },
    {
      key: "testimonialsModule", //redesign
      renderer: () => {
        return testimonialsModule ? (
          <SocialProof
            header={testimonialsModule.header.title}
            items={testimonialsModule.testimonials}
            linkText={testimonialsModule.header.ctaLinkText}
            linkUrl={testimonialsModule.header.ctaLinkUrl}
            eventConfig={courseDetailPageEventConfig}
            headerTextClassname={headerStylesInDetailPages}
            containerClassName=""
            isSideRailShown={isSideRailVisible}
          />
        ) : null;
      },
    },
    {
      key: "recentlyViewedProductCardList",
      renderer: () => (
        <RecentlyViewedProductCardList
          currentProductId={course.productUuid}
          showAllProducts={false}
          filterByPartner={titlePartner}
          displayLimit={4}
          minItemsToRender={2}
          surface="pdp"
          tagularPageType="Detail-Page"
        />
      ),
    },
    {
      key: "instructorList",
      renderer: () => {
        return course.activeCourseRun?.instructors &&
          course.activeCourseRun?.instructors.length > 0 ? (
          <InstructorList
            key="instructorList"
            instructors={course.activeCourseRun?.instructors}
            showSingleColumn={true}
          />
        ) : null;
      },
    },
    {
      key: "faqs",
      renderer: () => {
        return <FAQs key="faqs" course={course} variant="pathway" />;
      },
    },
    {
      key: "waysToTakeCourse",
      renderer: () => {
        return showCourseModes &&
          !isArchived &&
          course?.activeCourseRun?.isEnrollable ? (
          showRedesignedTrackComparison ? (
            <div
              className={maxWidthContainer}
              data-component="waysToTakeCourse"
              data-version="standard"
            >
              <CertificateWaysToTakeCourse
                key="certificateWaysToTakeCourse"
                activeCourseRun={course.activeCourseRun}
                course={course}
                tableDescription={tableDescription}
                hidePrice={hidePrice}
              >
                <div className="flex flex-col md:flex-row gap-2 sm:gap-3 items-center">
                  {coursePrice ? (
                    <CourseEnrollButton
                      buttonClass="flex-1"
                      courseRun={course.activeCourseRun!}
                      course={course}
                      location="cert-ways-to-take-course"
                      price={coursePrice}
                    />
                  ) : (
                    <EnrollLink
                      buttonClass="flex-1"
                      courseRun={course.activeCourseRun!}
                      course={course}
                      location="cert-ways-to-take-course"
                    />
                  )}
                  <AuditLink
                    courseRun={course.activeCourseRun}
                    course={course}
                    className="flex-1"
                  />
                </div>
              </CertificateWaysToTakeCourse>
            </div>
          ) : (
            <div
              className={mainContent}
              key="waysToTakeCourse"
              data-component="waysToTakeCourse"
              data-version="standard"
            >
              <WaysToTakeCourse
                activeCourseRun={course.activeCourseRun}
                course={course}
                layout="stacked"
              />
            </div>
          )
        ) : null;
      },
    },
    {
      key: "moreAboutCourse",
      renderer: () => {
        return course.additionalInformation ? (
          <MoreAboutCourse
            key="moreAboutCourse"
            additionalInformation={course.additionalInformation}
          />
        ) : null;
      },
    },
    {
      key: "socialShare",
      renderer: () => {
        return (
          <SocialShare
            key="socialShare"
            title={tCourse("shareThisCourse")}
            productUrl={course.productUrl}
            productName={course.productName}
          />
        );
      },
    },
    {
      key: "relatedPrograms",
      renderer: () => {
        if (!relatedPrograms) return null;
        const partnersForProgramSearch = course.partners.map(
          (partner) => partner.name,
        );
        return (
          <PartnerProgramRecommendations
            key="relatedPrograms"
            location="single-course"
            title={relatedPrograms?.title}
            subtitle={relatedPrograms?.subtitle}
            cardsToDisplay={relatedPrograms?.cardsToDisplay}
            partners={partnersForProgramSearch}
            pageType="single-course"
          />
        );
      },
    },
    {
      key: "bundleSavingsBanner",
      renderer: () => {
        return bundleSavingsBanner ? (
          <BundleSavingsBanner
            key="bundleSavingsBanner"
            title={bundleSavingsBanner?.title}
            titleEmphasis={bundleSavingsBanner?.titleEmphasis}
            badge={bundleSavingsBanner?.badge}
            description={bundleSavingsBanner?.description}
            discount={bundleSavingsBanner?.discount}
            bulletPoints={bundleSavingsBanner?.bulletPoints}
            cta={{
              label:
                bundleSavingsBanner?.cta?.label ||
                tProgram("exploreRelatedPrograms"),
              url: getProgramSearchUrl(course.partners),
              html_id: "bundling-cta",
              location: "single-course-detail-page",
              name: "single-course-bundling-CTA",
            }}
            compact
            partnerName={course.partners?.[0]?.name}
          />
        ) : null;
      },
    },
    {
      key: "productCompare",
      renderer: () => {
        const uuids = [
          course.productUuid,
          ...(productCompare?.uuids || compareFallbackUuids),
        ];

        return productCompare ? (
          <ProductCompare
            productCompare={productCompare}
            productUuids={uuids}
            currentCourseCheckoutUrl={compareCtaUrl}
            location="course-detail-page"
            hasStickyOffset={showSideRail ? "mobile-only" : undefined}
            currentCtaLabel={
              !isArchived && course.activeCourseRun?.isEnrollable
                ? (leadWithPaidData.value?.enrollCtaLabel ??
                  tCourse("advanceYourCareer"))
                : undefined
            }
          />
        ) : null;
      },
    },
  ];

  const sortedSections = getSortedComponentsBySectionOrder({
    componentList,
    sectionsOrder: mainContentFromTrafficConfig
      ? getMainContentSectionsOrder(mainContentFromTrafficConfig)
      : sectionsOrder,
  });

  return (
    <>
      {course.activeCourseRun && (
        <ProductViewTracker
          productId={course.activeCourseRun.key}
          productType="course"
          productName={course.productName}
          productUrl={course.productUrl}
          checkoutUrl={checkoutUrl}
          courseId={course?.productUuid}
          metadata={{
            price: coursePrice || undefined,
            currency: "USD",
            partner: course.partners?.[0]?.key,
            category: course.subjectEn ?? course.subjectEs,
            enrollmentStart: course.activeCourseRun.enrollmentStart?.toString(),
            enrollmentEnd: course.activeCourseRun.enrollmentEnd?.toString(),
            courseRunEnd: course.activeCourseRun.end?.toString(),
          }}
        />
      )}
      <div
        className={`${getBackgroundClass(headerBackground as HeaderBackground)} pb-12 pt-20 sm:py-12`}
      >
        <div
          className={`${maxWidthContainer} ${fullWidthContainer} gap-8 z-10`}
        >
          <div className="w-full flex justify-start">
            <Breadcrumbs
              path={course.productUrlSlug || ""}
              pageName={courseTitle || ""}
              linksToShowOnSmallScreen={1}
              containerClassName="not-prose"
              variant={breadcrumbVariant}
            />
          </div>
          <div className={showSideRail ? "xl:flex xl:gap-6 relative" : ""}>
            <div
              className={`flex flex-col gap-8 ${showSideRail ? "xl:max-w-[824px]" : ""}`}
            >
              {showSideRail && (
                <div className="md:hidden block">
                  <SideRailMobileContent
                    componentList={sideRailComponentList}
                    sideRailMobileBodyComponents={sideRailMobileBodyComponents}
                    sideRailMobileHeaderComponents={
                      sideRailMobileHeaderComponents
                    }
                  />
                </div>
              )}

              {/* Course Header - hidden on mobile, visible on md+ */}
              <div className={`${showSideRail && "hidden md:block"}`}>
                <CourseHeader
                  course={course}
                  variant={headerBackground === "light" ? "light" : "dark"}
                  pricingData={pricingData}
                />
              </div>

              {/* Factoid Bar - hidden on mobile, visible on md+ */}
              <div className="hidden md:block">
                <FactoidBar
                  courseData={course}
                  paymentOption={paymentOption}
                  leadWithPaidData={leadWithPaidData}
                  isFactoidsBarFieldsLoading={isLoading}
                  monarchFactoidsBar={factoidsBar}
                />
              </div>
            </div>

            <div className="hidden xl:block">
              {showSideRail && (
                <HeroSideRailContainer
                  productData={course}
                  courses={[]}
                  skus={[]}
                  pricingData={pricingData}
                  programIsEnrollable={
                    course.activeCourseRun?.isEnrollable || false
                  }
                  courseRun={course.activeCourseRun || undefined}
                  sideRailTitle={sideRailTitle}
                  sideRailImage={sideRailImage}
                  sideRailBulletList={sideRailBulletList}
                  sideRailHeaderComponents={sideRailHeaderComponents}
                  sideRailBodyComponents={sideRailBodyComponents}
                  sideRailProductTitle={sideRailProductTitle}
                  sideRailCollapsedHeaderComponents={
                    sideRailCollapsedHeaderComponents
                  }
                  sideRailCollapsedBodyComponents={
                    sideRailCollapsedBodyComponents
                  }
                  sideRailRating={sideRailRating}
                  sideRailSkillsList={sideRailSkillsList}
                  sideRailVideoPlayer={sideRailVideoPlayer}
                  sideRailImageTitle={sideRailImageTitle}
                  sideRailStatisticComponent={sideRailStatisticComponent}
                  straightToCheckout={straightToCheckout}
                  currencySelector={currencySelector}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <CourseCurrencyBarSurface
        course={course}
        pricingData={pricingData}
        courseIsEnrollable={course.activeCourseRun?.isEnrollable ?? false}
        isArchived={isArchived}
      />

      {course.activeCourseRun && (
        <StickyCTA
          course={course}
          courseRun={course.activeCourseRun}
          className={showSideRail ? "xl:hidden" : ""}
        />
      )}
      <div
        className="bg-putty-100 pt-12 md:pt-16 flex flex-col gap-12 sm:gap-16"
        id="course-details-body-container"
      >
        <div className={showSideRail ? "xl:w-[1280px] xl:mx-auto" : ""}>
          <div className={showSideRail ? mainContentSideRail : mainContent}>
            {sortedSections}
          </div>
          <div id="course-content-end"></div>
        </div>

        <div
          id="course-enterprise-cta"
          className="bg-putty-300"
          style={{
            maxWidth: "none",
            width: "100vw",
            marginLeft: "calc(-50vw + 50%)",
          }}
        >
          {!course.frequentlyAskedQuestions && course.hasOfacRestrictions && (
            <OFACMessage type="course" />
          )}
          <div className={maxWidthContainer}>
            <EnterpriseCTA productName={courseTitle} />
          </div>
        </div>
      </div>
    </>
  );
}
